import React, { useEffect, useState } from "react"
import {
    Box, Typography, Paper, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow
} from "@mui/material"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import dayjs from "dayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { ptBR as adapterptBR } from "dayjs/locale/pt-br"
import { ptBR as localePTBR } from "@mui/x-date-pickers/locales"
import api from "../../api"

const MetricCard = ({ icon, label, value, loading }) => (
    <Paper elevation={2} sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        p: 3,
        minWidth: 200,
        borderRadius: 3,
    }}>
        <Box sx={{ color: "#5399DB", display: "flex", alignItems: "center" }}>
            {icon}
        </Box>
        {loading
            ? <CircularProgress size={32} thickness={4} sx={{ color: "#5399DB" }} />
            : <Typography variant="h3" fontWeight="bold" sx={{ color: "#21446C", lineHeight: 1 }}>{value}</Typography>
        }
        <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>{label}</Typography>
    </Paper>
)

const ScoutPlansTable = ({ rows, loading }) => (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", alignSelf: "flex-start" }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#21446C" }}>
                Usuários Scout Ativos por Plano
            </Typography>
        </Box>
        <Table size="small">
            <TableHead>
                <TableRow sx={{ "& th": { fontWeight: "bold", color: "#21446C", borderBottom: "2px solid #e0e0e0" } }}>
                    <TableCell>Plano</TableCell>
                    <TableCell align="right">Ativos</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {loading
                    ? (
                        <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} sx={{ color: "#5399DB" }} />
                            </TableCell>
                        </TableRow>
                    )
                    : (rows ?? []).map(({ plan__name, count }) => (
                        <TableRow key={plan__name} hover>
                            <TableCell>{plan__name}</TableCell>
                            <TableCell align="right">
                                <Typography fontWeight={count > 0 ? "bold" : "normal"} sx={{ color: count > 0 ? "#21446C" : "#999" }}>
                                    {count}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))
                }
            </TableBody>
        </Table>
    </Paper>
)

const AdminDashboard = () => {
    const today = dayjs()
    const [selectedDate, setSelectedDate] = useState(today)
    const [metrics, setMetrics] = useState(null)
    const [loading, setLoading] = useState(true)
    const [noData, setNoData] = useState(false)

    useEffect(() => {
        setLoading(true)
        setNoData(false)
        setMetrics(null)

        const dateStr = selectedDate.format("YYYY-MM-DD")
        const todayStr = today.format("YYYY-MM-DD")
        const url = dateStr === todayStr
            ? "api/superadmin/dashboard/metrics/"
            : `api/superadmin/dashboard/metrics/?date=${dateStr}`

        api.get(url, { timeout: 15000 })
            .then(res => {
                if (res.status === 200) setMetrics(res.data)
                else if (res.status === 404) setNoData(true)
            })
            .finally(() => setLoading(false))
    }, [selectedDate])

    const isToday = selectedDate.format("YYYY-MM-DD") === today.format("YYYY-MM-DD")
    const dateLabel = isToday ? "Hoje" : selectedDate.format("DD/MM/YYYY")

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={adapterptBR}
            localeText={localePTBR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#21446C" }}>
                        Dashboard — {dateLabel}
                    </Typography>
                    <DatePicker
                        value={selectedDate}
                        onChange={val => val && setSelectedDate(val)}
                        disableFuture
                        slotProps={{ textField: { size: "small" } }}
                    />
                </Box>
                {noData
                    ? (
                        <Typography sx={{ color: "#999", fontStyle: "italic" }}>
                            Sem dados para esta data.
                        </Typography>
                    )
                    : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "flex-start" }}>
                            <MetricCard
                                icon={<PersonAddIcon sx={{ fontSize: 36 }} />}
                                label="Novos Cadastros"
                                value={metrics?.trial_signups_today ?? 0}
                                loading={loading}
                            />
                            <MetricCard
                                icon={<HourglassBottomIcon sx={{ fontSize: 36 }} />}
                                label="Em Trial"
                                value={metrics?.active_trials ?? 0}
                                loading={loading}
                            />
                            <MetricCard
                                icon={<AccountCircleIcon sx={{ fontSize: 36 }} />}
                                label="Contas Scout Ativas"
                                value={metrics?.scout_accounts ?? 0}
                                loading={loading}
                            />
                            <ScoutPlansTable rows={metrics?.active_scout_by_plan} loading={loading} />
                        </Box>
                    )
                }
            </Box>
        </LocalizationProvider>
    )
}

export default AdminDashboard
